import { orgSelectors } from 'ducks/orgs'
import useFilter from 'elements/controller/filter/useFilter'
import useQuery from 'elements/controller/query/useQuery'
import { CURRENT_FILTER_KEY_V2 } from 'elements/hardwareFilter/currentType/CurrentTypeFilterNodeFactoryV2'
import type { FilterValuesType } from 'elements/hardwareFilter/typeV2'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { ComponentTypesV2 } from 'types/selectComponent'
import useRecommendedAndRequiredComponentOrdering from '../useRecommendedAndRequiredComponentOrdering'
import type { HardwareSelectorControllerType } from './type'
import { omitConflictingCurrentTypeFilters } from './omitConflictingCurrentTypeFilters'
import useHardwareComponentsListBasic from './useHardwareComponentsListBasic'

export type HardwareFetchEngingSortByType = '_default_rule' | 'distributor_count'

const sanitizeCodesFilter = (codesValue: unknown): string | undefined => {
  if (typeof codesValue !== 'string') {
    return undefined
  }

  const sanitizedCodes = codesValue
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean)
    .filter((code) => code.toLowerCase() !== 'unknown')

  if (!sanitizedCodes.length) {
    return undefined
  }

  return sanitizedCodes.join(',')
}

// JSON.stringify(COMPONENT_TYPE_OPTIONS.map((option) => option.id))
/*
  This hook returns a list of hardware component collections along with the associated data and handlers used for obtaining the results.
*/
const useHardwareSelectorFetchEngine = ({
  componentTypes,
  limit,
  defaultFilterValues,
  persistentFilterValuesOverride,
  showArchived = false,
  sortBy = '_default_rule',
  blockRequestInitially = false,
  listAbortSignal,
}: {
  componentTypes: ComponentTypesV2[]
  limit: number
  showArchived?: boolean
  defaultFilterValues?: { [key: string]: any }
  persistentFilterValuesOverride?: { [key: string]: any }
  sortBy?: string
  blockRequestInitially?: boolean
  /** When set (e.g. shop home supplier switch), list fetches use AbortSignal instead of useGetList. */
  listAbortSignal?: AbortSignal
}): HardwareSelectorControllerType => {
  const [blockRequest, setBlockRequest] = useState(blockRequestInitially)
  const countryIso2 = useSelector(orgSelectors.getOrgIso2)
  const orderByCodes = useRecommendedAndRequiredComponentOrdering()
  const { query, queryPresenter } = useQuery({ perPage: limit, sort: sortBy, order: 'ASC' })
  const { filter, filterPresenter } = useFilter({
    defaultFilter: defaultFilterValues && {
      filterValues: {
        ...defaultFilterValues,
        country_iso2: countryIso2,
        order_by_codes: JSON.stringify(orderByCodes),
      },
      displayedFilters: {},
    },
    queryPresenter,
  })

  const modifiableFilterValues = filter.filterValues
  const sanitizedModifiableFilterValues = useMemo(() => {
    const nextFilterValues = { ...modifiableFilterValues }
    if ('codes' in nextFilterValues) {
      const sanitizedCodes = sanitizeCodesFilter(nextFilterValues.codes)
      if (sanitizedCodes) {
        nextFilterValues.codes = sanitizedCodes
      } else {
        delete nextFilterValues.codes
      }
    }
    return nextFilterValues
  }, [modifiableFilterValues])

  const PERSISTENT_FILTER_VALUES: FilterValuesType = useMemo(
    () => ({
      fieldset: 'list',
      types: JSON.stringify(componentTypes),
      ...(persistentFilterValuesOverride || {}),
      show_archived: showArchived,
    }),
    [persistentFilterValuesOverride, modifiableFilterValues, JSON.stringify(componentTypes)]
  )

  const allFilterValues = useMemo(
    () => ({
      page: query.page,
      perPage: query.perPage,
      sort: query.sort,
      order: query.order,
      ...PERSISTENT_FILTER_VALUES,
      ...sanitizedModifiableFilterValues,
    }),
    [query, sanitizedModifiableFilterValues, PERSISTENT_FILTER_VALUES]
  )

  const filterValues = useMemo(() => {
    const mergedFilters = {
      ...PERSISTENT_FILTER_VALUES,
      ...modifiableFilterValues,
    }
    const selectedCurrentType = String(mergedFilters[CURRENT_FILTER_KEY_V2] ?? '')
    return omitConflictingCurrentTypeFilters(mergedFilters, selectedCurrentType)
  }, [modifiableFilterValues, PERSISTENT_FILTER_VALUES])

  const { data, ids, total, loading, loaded } = useHardwareComponentsListBasic({
    resource: 'componentsv2',
    page: query.page,
    perPage: query.perPage,
    sort: query.sort,
    order: query.order,
    filterValues: filterValues,
    blockRequest,
    listAbortSignal,
  })

  const componentsData = useMemo(() => {
    const productData = []
    ids?.forEach((id) => {
      // @ts-ignore
      // TODO: add strong type
      data?.[id] && productData.push(data[id])
    })
    return productData
  }, [data, ids])

  return {
    setPage: queryPresenter.setPage,
    setPerPage: queryPresenter.setPerPage,
    // Default to immediate updates for "clicky" filters (radio/checkbox/select) so results refetch starts right away.
    // Search should explicitly pass `immediate=false` to keep debounced behavior.
    setFilters: (filterValues, displayedFilters, immediate = true) =>
      filterPresenter.setFilters(filterValues, displayedFilters, immediate),
    page: query.page,
    perPage: query.perPage,
    total,
    componentsData,
    loading,
    loaded,
    persistentFilterValues: PERSISTENT_FILTER_VALUES,
    modifiableFilterValues,
    allFilterValues,
    setBlockRequest,
  }
}

export default useHardwareSelectorFetchEngine
